/**
 * Client-side service to trigger asset optimization after upload
 * and resolve the correct LOD URL for a given model.
 */

import { getS3FileUrl } from '../config/aws';

const OPTIMIZER_WORKER_URL = import.meta.env.VITE_OPTIMIZER_WORKER_URL || '';

interface OptimizeResult {
  originalKey: string;
  optimizedKey: string;
  originalSize: number;
  optimizedSize: number;
  reductionPct: number;
  lods: Array<{ key: string; ratio: number; size: number }>;
}

/**
 * Trigger server-side optimization of an uploaded GLB file.
 * Returns URLs for optimized + LOD variants.
 * Falls back silently if no optimizer worker is configured.
 */
export async function optimizeAsset(
  key: string,
  options?: { maxTextureSize?: number; generateLods?: boolean }
): Promise<OptimizeResult | null> {
  if (!OPTIMIZER_WORKER_URL || !key.toLowerCase().endsWith('.glb')) {
    return null;
  }

  try {
    const res = await fetch(OPTIMIZER_WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        key,
        maxTextureSize: options?.maxTextureSize ?? 2048,
        generateLods: options?.generateLods ?? true,
      }),
    });

    if (!res.ok) {
      console.warn('[AssetOptimizer] Optimization failed:', await res.text());
      return null;
    }

    return await res.json();
  } catch (err) {
    console.warn('[AssetOptimizer] Optimization request failed:', err);
    return null;
  }
}

/**
 * Resolve the best URL for a model based on distance/quality.
 *
 * LOD strategy:
 * - distance < 10m: optimized (full quality)
 * - distance 10-25m: LOD 50%
 * - distance > 25m: LOD 25%
 *
 * Falls back to original URL if optimized variants don't exist.
 */
export function resolveLodUrl(originalKey: string, distanceHint?: 'high' | 'medium' | 'low'): string {
  const baseName = originalKey.replace(/\.glb$/i, '');
  const hint = distanceHint ?? 'high';

  switch (hint) {
    case 'low':
      return getS3FileUrl(`${baseName}-lod25.glb`);
    case 'medium':
      return getS3FileUrl(`${baseName}-lod50.glb`);
    case 'high':
    default:
      return getS3FileUrl(`${baseName}-optimized.glb`);
  }
}

/**
 * Check if an optimized variant exists for a given key.
 * Uses a HEAD request to check without downloading.
 */
export async function hasOptimizedVariant(originalKey: string): Promise<boolean> {
  const optimizedUrl = getS3FileUrl(originalKey.replace(/\.glb$/i, '-optimized.glb'));
  try {
    const res = await fetch(optimizedUrl, { method: 'HEAD' });
    return res.ok;
  } catch {
    return false;
  }
}
